/* eslint-disable @typescript-eslint/no-explicit-any -- DIRECT-239681 */

import { useEffect, useRef } from 'react';
import { isFunction } from '@direct-frontend/stdlib';
import { skipToken } from '@reduxjs/toolkit/query/react';

import { createQueryHook } from '../createQueryHook';
import type { AnyApi } from '../buildCreateApi';
import type { GetStoreFn } from '../types';
import type {
    ApiEndpointQuery,
    QueryActionCreatorResult,
    QueryArgFrom,
    QueryHooks,
    UseQueryStateOptions,
} from '../rtk-query-types';
import type { EndpointPrefetch, PrefetchKeyBuilder } from './buildEndpointPrefetch';
import { refetchQueriesEvent } from './events';
import { prefetchManager } from './prefetchManager';
import type { AnyEndpointDefinition, AnyQueryDefinition, UseQuerySubscriptionOptions } from './types';

/**
 * Создает отложенный промис для вызова `initiate`, чтобы избежать обновления стора
 * во время рендера компонента (что нарушает правила React и вызывает ошибку
 * "Cannot update a component while rendering a different component").
 *
 * @param initiate Функция для вызова endpoint.initiate()
 * @returns Промис-обертка с над initiate
 */
function initiateDeferred<Definition extends AnyQueryDefinition>(
    initiate: () => QueryActionCreatorResult<Definition>
): QueryActionCreatorResult<Definition> {
    let initiatePromise: QueryActionCreatorResult<Definition> | null = null;

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Приводим промис к нужному типу
    const wrappedPromise = new Promise(resolve => {
        setTimeout(() => {
            initiatePromise = initiate();
            initiatePromise.then(resolve);
        }, 0);
    }) as QueryActionCreatorResult<Definition>;

    function wrapMethod<Method extends keyof QueryActionCreatorResult<Definition> & string>(method: Method) {
        return function () {
            if (!initiatePromise) {
                throw new Error(`Cannot call "${method}" before initiate call`);
            }

            if (isFunction(initiatePromise[method])) {
                return initiatePromise[method]();
            }
        };
    }

    wrappedPromise.abort = wrapMethod('abort');
    wrappedPromise.refetch = wrapMethod('refetch');
    wrappedPromise.unsubscribe = wrapMethod('unsubscribe');
    wrappedPromise.unwrap = wrapMethod('unwrap');
    wrappedPromise.updateSubscriptionOptions = wrapMethod('updateSubscriptionOptions');

    function wrapProperty<Property extends keyof QueryActionCreatorResult<Definition> & string>(property: Property) {
        Object.defineProperty(wrappedPromise, property, {
            get() {
                if (!initiatePromise) {
                    throw new Error(`Cannot access "${property}" before initiate call`);
                }

                return initiatePromise[property];
            },
        });
    }

    wrapProperty('arg');
    wrapProperty('requestId');
    wrapProperty('queryCacheKey');
    wrapProperty('subscriptionOptions');

    return wrappedPromise;
}

/**
 * Проверяет является ли EndpointDefinition QueryDefinition.
 *
 * @internal
 */
export function isQueryDefinition(endpointDefinition: AnyEndpointDefinition): endpointDefinition is AnyQueryDefinition {
    return endpointDefinition.type === 'query';
}

// Параметры для `buildSSRCompatibleQueryHook()`.
type BuildSSRCompatibleQueryHookOptions = {
    api: AnyApi;
    endpointName: string;
    getStore: GetStoreFn;
    buildPrefetchKey: PrefetchKeyBuilder;
};

/**
 * Дополнительные параметры для UseQuery хуков, реализованные в модульном сторе.
 */
export type AdditionalUseQueryOptions = {
    /**
     * Количество секунд, в течении которых в сторе будет хранится подписка на данные,
     * полученные при ожидании отрисовки Suspense. При монтировании компонента, либо
     * по окончании указанного времени, подписка будет удалена.
     *
     * @default 60
     */
    keepPrefetchedDataFor?: number;
};

type QueryOptions = UseQuerySubscriptionOptions &
    UseQueryStateOptions<AnyQueryDefinition, any> &
    AdditionalUseQueryOptions;

// Метод для генерации useQuery хука, который поддерживает SSR и Suspense.
export function buildSSRCompatibleQueryHook({
    api,
    endpointName,
    getStore,
    buildPrefetchKey,
}: BuildSSRCompatibleQueryHookOptions) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    const endpoint = api.endpoints[endpointName] as unknown as ApiEndpointQuery<any, any> &
        QueryHooks<any> &
        EndpointPrefetch<any>;
    const useOriginalQuery = endpoint.useQuery;

    // Хук с поддержкой SSR и Suspense. По интерфейсу совпадает с базовым `useQuery` хуком.
    return createQueryHook(function useSsrCompatibleQuery(
        queryArgs: QueryArgFrom<AnyQueryDefinition>,
        queryOptions?: QueryOptions
    ) {
        // isLoading убираем из результата, так как вместо него у нас используется Suspense
        const { isLoading, ...result } = useOriginalQuery(queryArgs, {
            ...queryOptions,

            // Не позволяем включать эту опцию, так как она приводит к выполнению двух запросов
            // (один на этапе Suspense, второй при монтировании компонента)
            refetchOnMountOrArgChange: false,
        });

        /* eslint-disable react-hooks/rules-of-hooks -- DIRECT-239681 */
        if (process.env.BUILD_TARGET === 'client') {
            useRefetchEvent(queryArgs, result.refetch);
            useRefetchOnMountOrArgsChange(queryOptions?.refetchOnMountOrArgChange);
            useSuspenseCleanup(queryArgs);
        }
        /* eslint-enable react-hooks/rules-of-hooks */

        /**
         * Выкидываем промис при отсутствии данных в сторе.
         *
         * Данный хук должен быть последним хуком, вызываемым в `createQueryHook`,
         * после него НЕЛЬЗЯ добавлять вызовы хуков, иначе нарушается правила хуков.
         */
        useSuspense(queryArgs, queryOptions);

        return result;
    });

    /**
     * Проверяем данные в сторе и если они отсутствуют, то выкидываем промис для Suspense.
     *
     * @param queryArgs Параметры запроса
     * @param queryOptions Параметры хука
     */
    function useSuspense(queryArgs: QueryArgFrom<AnyQueryDefinition>, queryOptions?: QueryOptions) {
        const isSkipped = queryArgs === skipToken || Boolean(queryOptions?.skip);
        const { isSuspendable } = endpoint.useQueryState(queryArgs, {
            ...queryOptions,
            selectFromResult: isSkipped ? undefined : suspendableQueryStateSelector,
        });

        if (!isSkipped) {
            // Проверяем наличие промиса до проверки isSuspendable, чтобы даже при наличии
            // данных в кеше(isSuspendable = false) продлить prefetch-подписку.
            const prefetchKey = buildPrefetchKey(queryArgs);
            let promise = prefetchManager.get(prefetchKey);
            if (isSuspendable) {
                if (!promise) {
                    promise = initiateDeferred(() => getStore().dispatch(endpoint.initiate(queryArgs)));

                    prefetchManager.set(prefetchKey, {
                        promise,
                        keepPrefetchedDataFor: queryOptions?.keepPrefetchedDataFor,
                    });
                }

                throw promise;
            }
        }
    }

    /**
     * Удаляем подписку после монтирования компонента или изменения параметров запроса.
     * Подписка создается во время загрузки данных для Suspense, либо в результате вызова endpoint.prefetch().
     * На сервере подписки можно не удалять, так как хранение данных привязано к запросу.
     *
     * @param queryArgs Параметры запроса
     */
    function useSuspenseCleanup(queryArgs: QueryArgFrom<AnyQueryDefinition>) {
        const prefetchKey = queryArgs === skipToken ? undefined : buildPrefetchKey(queryArgs);
        useEffect(() => {
            // Если queryArgs === skipToken, то подписки не было создано и удалять нечего
            if (!prefetchKey) {
                return;
            }
            prefetchManager.remove(prefetchKey);
        }, [prefetchKey]);
    }

    /**
     * Подписываемся на refetchQueriesEvent, чтобы иметь возможность перезапросить все хуки, попадающие
     * под определенное условие. Может использоваться, например, чтобы перезапросить все запросы,
     * упавшие из-за капчи.
     *
     * @param queryArgs Параметры запроса
     * @param refetch Функция для обновления данных
     */
    function useRefetchEvent(queryArgs: QueryArgFrom<AnyQueryDefinition>, refetch: () => void) {
        // Перекладываем параметры запроса в ref, чтобы внутри события refetchQueriesEvent получить стейт для
        // самых актуальных параметров, не выполняя эффект заново.
        const queryArgsRef = useRef(queryArgs);
        queryArgsRef.current = queryArgs;

        useEffect(() => {
            const unsubscribe = refetchQueriesEvent.subscribe(condition => {
                const state = getStore().getState();
                const queryState = endpoint.select(queryArgsRef.current)(state);

                if (condition(queryState)) {
                    refetch();
                }
            });

            return unsubscribe;
        }, [refetch, queryArgsRef]);
    }

    /**
     * Кастомная реализация для параметра refetchOnMountOrArgChange, которая умеет работать в связке с Suspense.
     *
     * @param optionValue Значение параметра refetchOnMountOrArgChange
     */
    function useRefetchOnMountOrArgsChange(optionValue: boolean | number | undefined) {
        // eslint-disable-next-line @adv-frontend/config/pretty-todo-comments -- DIRECT-239681
        // TODO: DIRECT-163893
        if (process.env.NODE_ENV !== 'production' && optionValue) {
            throw new Error('Опция refetchOnMountOrArgChange не поддерживается в модульных сторах.');
        }
    }
}

type UseQueryStateDefaultResult = {
    isUninitialized: boolean;
    isLoading: boolean;
    data?: unknown;
    error?: unknown;
};

/**
 * Селектор для опции selectFromResult, который определяет нужно ли остановить рендер компонента через Suspense.
 *
 * @param state Стейт из хука useOriginalQuery
 */
function suspendableQueryStateSelector(state: UseQueryStateDefaultResult) {
    const isUninitialized = state.isUninitialized;
    const isLoading = isUninitialized ? state.data === undefined : state.isLoading;
    const hasError = Boolean(state.error);

    return {
        isSuspendable: isLoading && !hasError,
    };
}
